import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ScrollText, Sparkles, Moon, Sun, Trophy, Timer } from "lucide-react";
import { Section, Eyebrow } from "../components/site/Section";
import {
  getDrop, getArchiveStats, getActivity,
  getPersonalCount,
  NATION_BY_SLUG, RARITY_STYLES,
} from "../lib/archive";

// Mini countdown (mm:ss) for the next drop rotation.
function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!targetIso) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  if (!targetIso) return null;
  const ms = new Date(targetIso).getTime() - now;
  if (ms <= 0) return { mm: "00", ss: "00", expired: true };
  const totalSec = Math.floor(ms / 1000);
  const mm = String(Math.floor(totalSec / 60)).padStart(2, "0");
  const ss = String(totalSec % 60).padStart(2, "0");
  return { mm, ss, expired: false };
}

export default function AnimeNYCPage() {
  const [drop, setDrop] = useState(null);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState({ recent: [] });
  const [personalCount, setPersonalCount] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [d, s, a] = await Promise.all([getDrop(), getArchiveStats(), getActivity(4)]);
        if (!active) return;
        setDrop(d); setStats(s); setActivity(a || { recent: [] });
        setPersonalCount(getPersonalCount());
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 15000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const isConMode = drop?.mode === "con";
  const isVigil = drop?.phase === "vigil";
  const accent = isVigil ? "#9F7FE0" : "#C5A059";
  const countdown = useCountdown(isConMode ? drop?.expires_at : null);
  const total = stats?.total || 0;
  const communityCount = stats?.total_unlocked || 0;
  const pct = total ? Math.round((communityCount / total) * 100) : 0;
  const tickerItems = (activity?.recent || []).slice(0, 4);

  return (
    <div data-testid="anime-nyc-page">
      {/* ============ Hero ============ */}
      <Section testId="anyc-hero" className="pt-10 md:pt-20">
        <div className="ornate-rule text-[10px] tracking-[0.42em] uppercase text-[#C5A059]">
          <span>Welcome, Anime NYC · Aug 20 – 23</span>
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-4xl sm:text-5xl md:text-7xl text-[#EAE0D5] leading-[1] mt-5 max-w-4xl"
          data-testid="anyc-title"
        >
          Find your nation.
          <br />
          <span className="text-[#C5A059] italic font-light">Crack the Archive.</span>
          <br />
          <span className="text-[#EAE0D5]">Win the chain.</span>
        </motion.h1>
        <p className="mt-6 text-base md:text-lg text-[#A39E99] max-w-2xl leading-relaxed">
          Four days. <span data-testid="anyc-record-count-hero">{stats?.total || 60}</span> sealed records of the nine nations. Recover them
          all, return to the booth, answer one trivia question right —
          <span className="text-[#C5A059]"> walk out with $500 cash.</span>
        </p>

        {/* CTA buttons */}
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/nation-quiz"
            data-testid="anyc-cta-quiz"
            className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-7 py-4 text-xs tracking-[0.26em] uppercase hover:bg-[#D4AF68] transition-colors"
          >
            Start — Take the Rite <ArrowRight size={14} />
          </Link>
          <Link
            to="/archive"
            data-testid="anyc-cta-archive"
            className="inline-flex items-center gap-2 border border-[#C5A059]/40 text-[#C5A059] px-7 py-4 text-xs tracking-[0.26em] uppercase hover:bg-[#C5A059]/10 transition-colors"
          >
            Open the Archive <ScrollText size={14} />
          </Link>
        </div>
      </Section>

      {/* ============ Live status panel ============ */}
      <Section testId="anyc-live" className="pt-2">
        <div
          className="bg-[#0A0A0A] border p-6 md:p-9 relative overflow-hidden"
          style={{ borderColor: `${accent}40`, boxShadow: `0 0 70px -28px ${accent}` }}
          data-testid="anyc-live-panel"
        >
          {/* Phase + Live tag */}
          <div className="flex items-center justify-between flex-wrap gap-3 mb-7">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 border text-[10px] tracking-[0.32em] uppercase"
              style={{ borderColor: `${accent}60`, color: accent, background: `${accent}10` }}
            >
              {isVigil ? <Moon size={12} /> : <Sun size={12} />}
              {isConMode ? (isVigil ? "Night Vigil" : "Floor — Open") : "Con starts Aug 20"}
            </div>
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#A39E99]">
              <span className="relative inline-flex w-2 h-2">
                <span
                  className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping"
                  style={{ background: accent }}
                />
                <span
                  className="relative inline-flex rounded-full h-2 w-2"
                  style={{ background: accent }}
                />
              </span>
              {isConMode ? "Live" : "Standing By"}
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-8 md:gap-12">
            {/* Community */}
            <div className="md:col-span-7">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#A39E99] mb-2">
                Community Progress
              </div>
              <div className="flex items-baseline gap-3">
                <div
                  className="font-display text-5xl md:text-7xl tabular-nums leading-none"
                  style={{ color: accent }}
                  data-testid="anyc-community-count"
                >
                  {communityCount}
                </div>
                <div className="font-display text-2xl md:text-3xl text-[#737373]">
                  / {total} recovered
                </div>
              </div>
              <div className="w-full h-[3px] bg-white/5 mt-5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  className="h-full"
                  style={{ backgroundColor: accent }}
                />
              </div>
              {isConMode && countdown && !countdown.expired && (
                <div className="mt-5 inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[#C5A059] tabular-nums">
                  <Timer size={13} />
                  Next signal rotates in {countdown.mm}:{countdown.ss}
                </div>
              )}
            </div>

            {/* Personal */}
            <div className="md:col-span-5">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#A39E99] mb-2">
                Your Record
              </div>
              <div className="flex items-baseline gap-3">
                <div
                  className="font-display text-5xl md:text-6xl tabular-nums leading-none text-[#EAE0D5]"
                  data-testid="anyc-personal-count"
                >
                  {personalCount}
                </div>
                <div className="font-display text-2xl text-[#737373]">/ {total}</div>
              </div>
              <p className="text-[11px] text-[#737373] mt-3 leading-relaxed">
                Only records you personally crack count toward the $500 contest.
                Saved locally on this device.
              </p>
            </div>
          </div>

          {/* Recent activity */}
          {tickerItems.length > 0 && (
            <div className="mt-7 pt-5 border-t border-white/5" data-testid="anyc-ticker">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#A39E99] mb-3">
                Recent recoveries
              </div>
              <ul className="space-y-1.5">
                {tickerItems.map((it) => {
                  const nation = NATION_BY_SLUG[it.nation];
                  const rstyle = RARITY_STYLES[it.rarity] || RARITY_STYLES.C;
                  return (
                    <li key={it.id} className="flex items-center gap-3 text-sm">
                      <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: rstyle.color }}
                      />
                      <span className="text-[#EAE0D5]">{it.name}</span>
                      <span className="text-[10px] tracking-[0.22em] uppercase text-[#737373]">
                        {nation?.name || it.nation}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </Section>

      {/* ============ The Rules ============ */}
      <Section testId="anyc-rules" className="pt-0">
        <div className="ornate-rule text-[10px] tracking-[0.4em] uppercase text-[#C5A059] mb-8">
          <span>The Hunt — How It Works</span>
        </div>
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {[
            {
              n: "01",
              t: "Take the Rite",
              d: "A three-minute personality quiz reveals which of the nine nations claims you. You'll know your nation, your strengths, your shadow, and the first character you'll meet.",
              icon: <Sparkles size={18} />,
            },
            {
              n: "02",
              t: "Crack the Records",
              d: "Each hour a new sealed figure surfaces. Guess their nation right and they join the Cast — wrong, and you're locked out of that figure until the next rotation. Every correct guess in the room moves the community counter.",
              icon: <ScrollText size={18} />,
            },
            {
              n: "03",
              t: "Win the Chain",
              d: "Recover every record before Sunday closes. Present your phone at the booth. Answer one trivia question about the world. Walk out $500 richer.",
              icon: <Trophy size={18} />,
            },
          ].map((step) => (
            <div
              key={step.n}
              data-testid={`anyc-step-${step.n}`}
              className="bg-[#0A0A0A] border border-white/5 p-6 md:p-7 hover:border-[#C5A059]/40 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059]">
                  Step {step.n}
                </div>
                <span className="text-[#C5A059]">{step.icon}</span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-[#EAE0D5] mt-3 leading-tight">
                {step.t}
              </h3>
              <p className="text-sm text-[#A39E99] mt-3 leading-relaxed">{step.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ============ Prize block ============ */}
      <Section testId="anyc-prize" className="pt-0 pb-20">
        <div
          className="bg-[#0A0A0A] border border-[#C5A059]/40 p-8 md:p-12 text-center"
          style={{ boxShadow: "0 0 80px -28px #C5A059" }}
        >
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#C5A059]">
            The Reward
          </div>
          <div className="font-display text-5xl md:text-7xl text-[#EAE0D5] mt-4 leading-none">
            <span className="text-[#C5A059]">$500</span> cash
          </div>
          <p className="text-sm md:text-base text-[#A39E99] mt-5 max-w-xl mx-auto leading-relaxed">
            One winner. The first scanner to recover all <span data-testid="anyc-record-count-prize">{stats?.total || 60}</span> records, return to
            the booth, and answer the trivia question correctly walks home with
            the prize — and their name written into the lore.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/nation-quiz"
              data-testid="anyc-prize-cta-quiz"
              className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-7 py-4 text-xs tracking-[0.26em] uppercase hover:bg-[#D4AF68]"
            >
              Start — Take the Rite <ArrowRight size={14} />
            </Link>
            <Link
              to="/archive"
              data-testid="anyc-prize-cta-archive"
              className="inline-flex items-center gap-2 border border-[#C5A059]/40 text-[#C5A059] px-7 py-4 text-xs tracking-[0.26em] uppercase hover:bg-[#C5A059]/10"
            >
              Open the Archive <ScrollText size={14} />
            </Link>
          </div>
          <p className="mt-5 text-[10px] tracking-[0.28em] uppercase text-[#737373]">
            One winner · Trivia must be answered in person · Tally tracked per device
          </p>
        </div>
      </Section>
    </div>
  );
}
